import { easyCheckRequest } from '../api/config';
import easyCheckApi from '../api/config';
import * as XLSX from 'xlsx-js-style';

export const beneficiaryImportService = {
  // Generate Excel template for beneficiary import
  generateTemplate: () => {
    const templateData = [
      {
        'Beneficiary Name': 'DUSAT General Trading Dubai',
        'Company ID': '4',
        'Created Date': '2025-01-01T00:00:00',
        'Created User': 'ak',
        'Modified Date': '2025-01-01T00:00:00',
        'Modified User': 'ak',
        'Is Active': 'true'
      }
    ];

    const ws = XLSX.utils.json_to_sheet(templateData, {
      header: Object.keys(templateData[0])
    });

    // Add instructions sheet
    const instructions = [
      { Field: 'Beneficiary Name', Description: 'Name of the beneficiary (Required)', Example: 'DUSAT General Trading Dubai' },
      { Field: 'Company ID', Description: 'Company identifier (Required)', Example: '4' },
      { Field: 'Created Date', Description: 'Creation date (Optional)', Example: '2025-01-01T00:00:00' },
      { Field: 'Created User', Description: 'User who created the record (Optional)', Example: 'ak' },
      { Field: 'Modified Date', Description: 'Last modification date (Optional)', Example: '2025-01-01T00:00:00' },
      { Field: 'Modified User', Description: 'User who last modified (Optional)', Example: 'ak' },
      { Field: 'Is Active', Description: 'Active status (Optional, default: true)', Example: 'true/false' }
    ];

    const instructionsWs = XLSX.utils.json_to_sheet(instructions);

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, instructionsWs, 'Instructions');
    XLSX.utils.book_append_sheet(wb, ws, 'Beneficiary Template');

    // Set column widths
    const wscols = [
      { wch: 30 }, // Beneficiary Name
      { wch: 15 }, // Company ID
      { wch: 20 }, // Created Date
      { wch: 15 }, // Created User
      { wch: 20 }, // Modified Date
      { wch: 15 }, // Modified User
      { wch: 12 }  // Is Active
    ];
    ws['!cols'] = wscols;

    const instructionsCols = [
      { wch: 20 }, // Field
      { wch: 50 }, // Description
      { wch: 30 }  // Example
    ];
    instructionsWs['!cols'] = instructionsCols;

    return wb;
  },

  // Download template file
  downloadTemplate: () => {
    const wb = beneficiaryImportService.generateTemplate();
    const fileName = `Beneficiary_Master_Template_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
  },

  // Validate Excel file structure
  validateFileStructure: (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          
          if (!workbook.SheetNames.includes('Beneficiary Template')) {
            reject(new Error('Excel file must contain a "Beneficiary Template" sheet'));
            return;
          }

          const worksheet = workbook.Sheets['Beneficiary Template'];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);

          if (jsonData.length === 0) {
            reject(new Error('Excel file is empty'));
            return;
          }

          const requiredFields = ['Beneficiary Name', 'Company ID'];
          const firstRow = jsonData[0];
          const missingFields = requiredFields.filter(field => !(field in firstRow));

          if (missingFields.length > 0) {
            reject(new Error(`Missing required columns: ${missingFields.join(', ')}`));
            return;
          }

          resolve({
            isValid: true,
            data: jsonData,
            rowCount: jsonData.length
          });
        } catch (error) {
          reject(new Error('Invalid Excel file format'));
        }
      };
      reader.readAsArrayBuffer(file);
    });
  },

  // Process and import beneficiary data
  importBeneficiaries: async (file, onProgress) => {
    try {
      // First validate the file
      const validation = await beneficiaryImportService.validateFileStructure(file);
      
      if (!validation.isValid) {
        throw new Error('File validation failed');
      }

      const { data } = validation;
      const results = [];
      const totalRows = data.length;

      for (let i = 0; i < data.length; i++) {
        const row = data[i];
        const rowNumber = i + 1;

        try {
          // Update progress
          if (onProgress) {
            onProgress({
              current: i + 1,
              total: totalRows,
              percentage: Math.round(((i + 1) / totalRows) * 100),
              status: `Processing row ${i + 1} of ${totalRows}...`
            });
          }

          // Validate required fields
          const validationErrors = [];
          if (!row['Beneficiary Name']) validationErrors.push('Beneficiary Name is required');
          if (!row['Company ID']) validationErrors.push('Company ID is required');

          // Validate Company ID is numeric
          if (row['Company ID'] && isNaN(Number(row['Company ID']))) {
            validationErrors.push('Company ID must be a number');
          }

          if (validationErrors.length > 0) {
            results.push({
              rowNumber,
              data: row,
              status: 'FAILED',
              errors: validationErrors,
              message: validationErrors.join('; ')
            });
            continue;
          }

          // Prepare beneficiary data for API
          const beneficiaryData = {
            beneficiaryName: row['Beneficiary Name'],
            companyId: Number(row['Company ID']),
            createdDate: row['Created Date'] || new Date().toISOString(),
            createdUser: row['Created User'] || 'system',
            modifiedDate: row['Modified Date'] || new Date().toISOString(),
            modifiedUser: row['Modified User'] || 'system',
            isActive: row['Is Active'] !== undefined ? String(row['Is Active']).toLowerCase() === 'true' : true
          };

          // Call API to create beneficiary
          const result = await easyCheckRequest(() => 
            easyCheckApi.post('/beneficiarymaster', beneficiaryData)
          );

          if (result.success) {
            results.push({
              rowNumber,
              data: row,
              status: 'SUCCESS',
              message: 'Beneficiary created successfully',
              beneficiaryId: result.data?.id
            });
          } else {
            results.push({
              rowNumber,
              data: row,
              status: 'FAILED',
              message: result.message || 'Failed to create beneficiary',
              errors: [result.message || 'Unknown error']
            });
          }

        } catch (error) {
          results.push({
            rowNumber,
            data: row,
            status: 'FAILED',
            message: error.message || 'Unexpected error occurred',
            errors: [error.message || 'Unexpected error']
          });
        }

        // Add a small delay to prevent overwhelming the API
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      return {
        success: true,
        results,
        summary: {
          total: totalRows,
          successful: results.filter(r => r.status === 'SUCCESS').length,
          failed: results.filter(r => r.status === 'FAILED').length
        }
      };

    } catch (error) {
      return {
        success: false,
        message: error.message,
        results: []
      };
    }
  },

  // Download results as Excel file
  downloadResultFile: (results, originalFileName) => {
    const resultsData = results.map(result => ({
      'Row Number': result.rowNumber,
      'Beneficiary Name': result.data['Beneficiary Name'],
      'Company ID': result.data['Company ID'],
      'Status': result.status,
      'Message': result.message,
      'Errors': result.errors ? result.errors.join('; ') : '',
      'Beneficiary ID': result.beneficiaryId || ''
    }));

    const ws = XLSX.utils.json_to_sheet(resultsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Import Results');

    // Set column widths
    const wscols = [
      { wch: 12 }, // Row Number
      { wch: 30 }, // Beneficiary Name
      { wch: 15 }, // Company ID
      { wch: 10 }, // Status
      { wch: 40 }, // Message
      { wch: 40 }, // Errors
      { wch: 15 }  // Beneficiary ID
    ];
    ws['!cols'] = wscols;

    const fileName = `Beneficiary_Import_Results_${originalFileName.replace('.xlsx', '')}_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
  }
}; 